import { Dispatch, PropsWithChildren, SetStateAction, useState } from 'react'
import { FileUploader } from 'react-drag-drop-files'
import { useTranslation } from 'react-i18next'

import { ConfirmDialog } from '@/modules/shared/components/confirm-dialog/ConfirmDialog'
import { CloseIcon } from '@/modules/shared/icons/CloseIcon'
import { UploadIcon } from '@/modules/shared/icons/UploadIcon'
import { Themes } from '@/modules/shared/types'

interface UploadFileProps extends PropsWithChildren {
  selectedFile: File | null
  setSelectedFile: Dispatch<SetStateAction<File | null>>
  fileTypes?: string[]
  /** When true, file selection and drop are not allowed. */
  disabled?: boolean
}

export default function UploadFile({ selectedFile, setSelectedFile, fileTypes, children, disabled }: UploadFileProps) {
  const { t } = useTranslation()
  const [showTypeError, setShowTypeError] = useState(false)
  const [showSizeError, setShowSizeError] = useState(false)

  return (
    <>
      {!selectedFile ? (
        disabled ? (
          <div
            className="mt-3 block w-full cursor-not-allowed rounded-md border-2 border-dashed border-gray-300 bg-gray-100 px-5 py-8 text-center opacity-50"
            aria-disabled="true"
          >
            {children ? (
              children
            ) : (
              <>
                <UploadIcon className="mx-auto size-6 text-gray-500" />
                <p className="mt-2 text-gray-500">
                  {t('attachmentModal.dragUpload', 'Drag and drop a file here or click to browse your device')}
                </p>
              </>
            )}
          </div>
        ) : (
          <FileUploader
            handleChange={(e: File | File[]) => {
              // For single file upload, handle both File and File[] cases
              const file = Array.isArray(e) ? e[0] : e
              setSelectedFile(file)
            }}
            name="file"
            maxSize={5}
            dropMessageStyle={{ color: 'rgba(0, 0, 0, 0)' }}
            types={fileTypes}
            onTypeError={() => {
              if (fileTypes) {
                setShowTypeError(true)
              }
            }}
            onSizeError={() => setShowSizeError(true)}
          >
            <div className="mt-3 block w-full cursor-pointer rounded-md border-2 border-dashed border-gray-300 bg-gray-100 px-5 py-8 text-center">
              {children ? (
                children
              ) : (
                <>
                  <UploadIcon className="mx-auto size-6 text-gray-500" />
                  <p className="mt-2 text-gray-500">
                    {t('attachmentModal.dragUpload', 'Drag and drop a file here or click to browse your device')}
                  </p>
                </>
              )}
            </div>
          </FileUploader>
        )
      ) : (
        <div className="mt-3 flex items-center justify-between rounded-md border bg-white p-4">
          {selectedFile?.name}
          <CloseIcon
            className="size-6 cursor-pointer text-gray-500 hover:text-gray-700"
            onClick={() => setSelectedFile(null)}
          />
        </div>
      )}
      <ConfirmDialog
        title={t('fileError.tooLargeTitle', 'File Size Too Large')}
        description={t(
          'fileError.tooLargeDescription',
          'The file you are attempting to upload is too large. Please try again.'
        )}
        primaryButtonLabel={t('general.okay', 'Okay')}
        isOpen={showSizeError}
        setIsOpen={setShowSizeError}
        onPrimaryBtnClick={() => setShowSizeError(false)}
        theme={Themes.Error}
      />
      <ConfirmDialog
        title={t('fileError.wrongTypeTitle', 'File Type Not Supported')}
        description={t(
          'fileError.wrongTypeDescription',
          'The file you are attempting to upload is the wrong file type. Please try again. Supported file types: {{ fileTypes }}',
          { fileTypes: fileTypes?.map((type) => type.toUpperCase()).join(', ') }
        )}
        primaryButtonLabel={t('general.okay', 'Okay')}
        isOpen={showTypeError}
        setIsOpen={setShowTypeError}
        onPrimaryBtnClick={() => setShowTypeError(false)}
        theme={Themes.Error}
      />
    </>
  )
}
